import React from "react";
import { Play, Users, Sparkles, TrendingUp, Heart, Clock } from "lucide-react";
import { useArenaHighlights } from "@/hooks/useArenaHighlights";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

interface ArenaHighlightsFeedProps {
  className?: string;
}

export const ArenaHighlightsFeed: React.FC<ArenaHighlightsFeedProps> = ({ className }) => {
  const { highlights, loading } = useArenaHighlights();

  const getHighlightBadge = (type: string) => {
    switch (type) {
      case "trending":
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-xs font-medium">
            <TrendingUp className="w-3 h-3" />
            Trending
          </div>
        );
      case "match":
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-pink-500 text-white text-xs font-medium">
            <Heart className="w-3 h-3" />
            Match Made
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/80 text-white text-xs font-medium">
            <Sparkles className="w-3 h-3" />
            Moment
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pop" />
            Arena Highlights
          </h3>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="shrink-0 w-64 rounded-2xl overflow-hidden bg-muted animate-pulse">
              <div className="aspect-video bg-muted-foreground/20" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
                <div className="h-3 bg-muted-foreground/20 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pop" />
          Arena Highlights
        </h3>
        {highlights.length > 0 && (
          <span className="text-xs text-muted-foreground">Last 24 hours</span>
        )}
      </div>

      {highlights.length > 0 ? (
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
          {highlights.map((highlight) => (
            <div
              key={highlight.id}
              className="relative shrink-0 w-64 rounded-2xl overflow-hidden bg-card shadow-lg group cursor-pointer"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video">
                {highlight.thumbnail_url ? (
                  <img
                    src={highlight.thumbnail_url}
                    alt={highlight.arena_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-primary/50" />
                  </div>
                )}
                
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-6 h-6 text-primary fill-primary ml-0.5" />
                  </div>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded bg-black/60 text-white text-xs">
                  <Clock className="w-3 h-3" />
                  {highlight.duration_seconds}s
                </div>

                {/* Type badge */}
                <div className="absolute top-2 left-2">
                  {getHighlightBadge(highlight.highlight_type)}
                </div>
              </div>

              {/* Info */}
              <div className="p-3">
                <h4 className="font-semibold text-sm truncate">{highlight.arena_name}</h4>
                <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-0.5">
                      <Users className="w-3 h-3" />
                      {highlight.participant_count}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Sparkles className="w-3 h-3 text-pop" />
                      {highlight.pop_count}
                    </span>
                  </div>
                  <span>{formatDistanceToNow(new Date(highlight.created_at), { addSuffix: true })}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Sparkles className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No highlights yet</p>
          <p className="text-sm">Join an arena to create moments!</p>
        </div>
      )}
    </div>
  );
};

export default ArenaHighlightsFeed;
